export type TailorEvidenceMode = "raw" | "compatible" | "corrected";
export type TailorTrustStatus = "trusted" | "flagged" | "rejected";

export interface TailorAxisInput {
  id: string;
  label: string;
  min: number | null;
  max: number | null;
  mean: number | null;
  held_default?: number | null;
  sample_count?: number;
}

export interface TailorTrustLimits {
  hiddenRmsLimit: number;
  hiddenMaxDeltaLimit: number;
  correctionAbsLimit: number;
  correctionRelativeLimit: number;
  compatibleRmsLimit: number;
  compatibleMaxDeltaLimit: number;
}

export interface TailorSliceModel {
  output: string;
  axes: TailorAxisInput[];
  coefficients: number[];
  rmse: number | null;
  trainCount: number;
  outputMean: number;
  outputScale: number;
}

export interface TailorCorrectionAudit {
  rawValue: number;
  modelAtRaw: number;
  modelAtSlice: number;
  correctedValue: number;
  correctionDelta: number;
  hiddenRms: number;
  hiddenMaxDelta: number;
  trustScore: number;
  status: TailorTrustStatus;
  compatible: boolean;
}

export interface TailorEvidenceSummary {
  raw: number;
  compatible: number;
  correctedTotal: number;
  correctedTrusted: number;
  correctedFlagged: number;
  correctedRejected: number;
  meanHiddenRms: number | null;
  maxHiddenJump: number | null;
  maxCorrectionDelta: number | null;
}

export interface TailorSuggestionMove {
  axis: string;
  label: string;
  value: number;
  delta: number;
}

export interface TailorSuggestionEvidence {
  /** Nearby rows whose corrected transport to this candidate is trusted (or directly compatible). */
  trusted: number;
  flagged: number;
  rejected: number;
}

export interface TailorRunSuggestion {
  id: string;
  xAxis: string;
  yAxis: string;
  values: Record<string, number>;
  predicted: number;
  nearestDistance: number;
  /** Pane distance to the nearest row whose corrected transport to this candidate is trusted — Infinity-capped when nothing trustworthy reaches it. */
  nearestTrustedDistance: number;
  score: number;
  reason: string;
  /** RMS normalized distance of the hidden axes from the current held slice — 0 means the suggestion stays on the visible 2D pane. */
  sliceDistance: number;
  /** Dominant hidden-axis moves relative to the held slice, largest first. */
  moves: TailorSuggestionMove[];
  /** raw/compatible/corrected audit of the evidence closest to this candidate. */
  evidence: TailorSuggestionEvidence;
}

/** A point on the model-derived departure frontier, in raw axis units. */
export interface TailorRidgePoint {
  x: number;
  y: number;
  /** Fitted response at (x, y) on the held slice — the frontier rides the surface. */
  predicted: number;
  /** Normalized 0..1 departure strength at this station (1 = steepest break on the scan). */
  departure: number;
}

export interface TailorFeatureRidge {
  xAxis: string;
  yAxis: string;
  output: string;
  /** Polyline of the frontier across the Y focus axis, raw units. */
  points: TailorRidgePoint[];
  /** Dominant break location on the X focus axis (raw units) — where the cheap trend fails hardest. */
  crestX: number;
  /**
   * 0..1 confidence the frontier is real, NOT a measurement.
   * Folds together how hard the fitted response bends away from its own linear
   * trend (the curvature the quadratic term carries) and whether Tailor's
   * high-score suggestions cluster on it. Low bend -> low confidence -> the UI
   * should label it faint/advisory, never as a measured stall line.
   */
  confidence: number;
  /** How the frontier was derived — surfaced verbatim in the UI so it can never read as measured. */
  derivation: "model-quadratic-departure";
  /** Human-readable caption matching the raw/compatible/corrected honesty conventions. */
  caption: string;
}

export const DEFAULT_TAILOR_TRUST_LIMITS: TailorTrustLimits = {
  hiddenRmsLimit: 0.18,
  hiddenMaxDeltaLimit: 0.36,
  correctionAbsLimit: 0.18,
  correctionRelativeLimit: 0.38,
  compatibleRmsLimit: 0.075,
  compatibleMaxDeltaLimit: 0.18,
};

function numericValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function axisDefault(axis: TailorAxisInput): number {
  return numericValue(axis.held_default) ?? numericValue(axis.mean) ?? numericValue(axis.min) ?? 0;
}

function axisSpan(axis: TailorAxisInput): number {
  const min = numericValue(axis.min);
  const max = numericValue(axis.max);
  if (min !== null && max !== null && Math.abs(max - min) > 1e-12) return Math.abs(max - min);
  return 1;
}

/** Genuine measured span (no 1.0 fallback) — null when the axis has no usable range. */
function measuredAxisSpan(axis: TailorAxisInput): number {
  const min = numericValue(axis.min);
  const max = numericValue(axis.max);
  if (min === null || max === null) return 0;
  return Math.abs(max - min);
}

function axisRaw(row: Record<string, unknown>, axis: TailorAxisInput): number {
  return numericValue(row[axis.id]) ?? axisDefault(axis);
}

function axisNorm(value: number, axis: TailorAxisInput): number {
  return (value - axisDefault(axis)) / axisSpan(axis);
}

function featureVector(row: Record<string, unknown>, axes: TailorAxisInput[]): number[] {
  const values = [1];
  for (const axis of axes) {
    const z = axisNorm(axisRaw(row, axis), axis);
    values.push(z);
  }
  for (const axis of axes) {
    const z = axisNorm(axisRaw(row, axis), axis);
    values.push(z * z);
  }
  return values;
}

function solveLinearSystem(matrix: number[][], vector: number[]): number[] | null {
  const n = vector.length;
  const a = matrix.map((row, index) => [...row, vector[index] ?? 0]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(a[row]![col]!) > Math.abs(a[pivot]![col]!)) pivot = row;
    }
    if (Math.abs(a[pivot]![col]!) < 1e-10) return null;
    if (pivot !== col) [a[pivot], a[col]] = [a[col]!, a[pivot]!];
    const denom = a[col]![col]!;
    for (let item = col; item <= n; item += 1) a[col]![item]! /= denom;
    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = a[row]![col]!;
      if (Math.abs(factor) < 1e-14) continue;
      for (let item = col; item <= n; item += 1) {
        a[row]![item]! -= factor * a[col]![item]!;
      }
    }
  }
  return a.map((row) => row[n] ?? 0);
}

export function fitTailorSliceModel(
  rows: Record<string, unknown>[],
  axes: TailorAxisInput[],
  output: string,
  maxTrainRows = 1600,
): TailorSliceModel | null {
  const usableAxes = axes
    .filter((axis) => axis.sample_count == null || axis.sample_count >= 3)
    // genuine measured span — axisSpan() floors at 1.0 (for axisNorm's
    // divide-by-zero guard), so filtering on it never drops anything. Drop
    // axes the campaign never moved (min===max) or that have no range.
    .filter((axis) => measuredAxisSpan(axis) > 1e-12)
    .slice(0, 18);
  const samples = rows.filter((row) => numericValue(row[output]) !== null);
  if (usableAxes.length < 1 || samples.length < Math.max(8, usableAxes.length + 3)) return null;
  const stride = Math.max(1, Math.ceil(samples.length / maxTrainRows));
  const training = samples.filter((_, index) => index % stride === 0).slice(0, maxTrainRows);
  const termCount = 1 + usableAxes.length * 2;
  const ata = Array.from({ length: termCount }, () => Array.from({ length: termCount }, () => 0));
  const atb = Array.from({ length: termCount }, () => 0);
  let ySum = 0;
  for (const row of training) {
    const y = numericValue(row[output])!;
    ySum += y;
    const features = featureVector(row, usableAxes);
    for (let i = 0; i < termCount; i += 1) {
      atb[i]! += features[i]! * y;
      for (let j = 0; j < termCount; j += 1) ata[i]![j]! += features[i]! * features[j]!;
    }
  }
  const ridge = Math.max(1e-6, training.length * 1e-4);
  for (let i = 1; i < termCount; i += 1) ata[i]![i]! += ridge;
  const coefficients = solveLinearSystem(ata, atb);
  if (!coefficients) return null;
  let err = 0;
  let count = 0;
  for (const row of training) {
    const y = numericValue(row[output]);
    const pred = predictTailorSliceModel({ axes: usableAxes, coefficients }, row);
    if (y === null || !Number.isFinite(pred)) continue;
    err += (pred - y) ** 2;
    count += 1;
  }
  const outputMean = ySum / training.length;
  const outputValues = training.map((row) => numericValue(row[output])).filter((value): value is number => value !== null);
  const outputMin = Math.min(...outputValues);
  const outputMax = Math.max(...outputValues);
  return {
    output,
    axes: usableAxes,
    coefficients,
    trainCount: training.length,
    rmse: count ? Math.sqrt(err / count) : null,
    outputMean,
    outputScale: Math.max(1e-9, outputMax - outputMin),
  };
}

export function predictTailorSliceModel(
  model: Pick<TailorSliceModel, "axes" | "coefficients">,
  row: Record<string, unknown>,
  overrides: Record<string, number> = {},
): number {
  const patched = overrides && Object.keys(overrides).length ? { ...row, ...overrides } : row;
  const features = featureVector(patched, model.axes);
  return features.reduce((sum, value, index) => sum + value * (model.coefficients[index] ?? 0), 0);
}

export function correctionForPair(
  row: Record<string, unknown>,
  model: TailorSliceModel | null,
  output: string,
  heldValues: Record<string, number>,
  xKey: string,
  yKey: string,
  trust: TailorTrustLimits = DEFAULT_TAILOR_TRUST_LIMITS,
): TailorCorrectionAudit | null {
  if (!model) return null;
  const rawValue = numericValue(row[output]);
  if (rawValue === null) return null;
  const hiddenAxes = model.axes.filter((axis) => axis.id !== xKey && axis.id !== yKey);
  let sum = 0;
  let maxDelta = 0;
  const overrides: Record<string, number> = {};
  for (const axis of hiddenAxes) {
    const held = numericValue(heldValues[axis.id]) ?? axisDefault(axis);
    const delta = Math.abs(axisNorm(axisRaw(row, axis), axis) - axisNorm(held, axis));
    sum += delta * delta;
    maxDelta = Math.max(maxDelta, delta);
    overrides[axis.id] = held;
  }
  const hiddenRms = hiddenAxes.length ? Math.sqrt(sum / hiddenAxes.length) : 0;
  const modelAtRaw = predictTailorSliceModel(model, row);
  const modelAtSlice = predictTailorSliceModel(model, row, overrides);
  const correctionDelta = modelAtSlice - modelAtRaw;
  const correctedValue = rawValue + correctionDelta;
  const correctionLimit = Math.max(trust.correctionAbsLimit, Math.abs(rawValue) * trust.correctionRelativeLimit);
  const trustScore = Math.max(
    hiddenRms / Math.max(1e-9, trust.hiddenRmsLimit),
    maxDelta / Math.max(1e-9, trust.hiddenMaxDeltaLimit),
    Math.abs(correctionDelta) / Math.max(1e-9, correctionLimit),
  );
  const status: TailorTrustStatus = trustScore <= 1 ? "trusted" : trustScore <= 1.75 ? "flagged" : "rejected";
  return {
    rawValue,
    modelAtRaw,
    modelAtSlice,
    correctedValue,
    correctionDelta,
    hiddenRms,
    hiddenMaxDelta: maxDelta,
    trustScore,
    status,
    compatible: hiddenRms <= trust.compatibleRmsLimit && maxDelta <= trust.compatibleMaxDeltaLimit,
  };
}

export function tailorValueForMode(
  mode: TailorEvidenceMode,
  row: Record<string, unknown>,
  model: TailorSliceModel | null,
  output: string,
  heldValues: Record<string, number>,
  xKey: string,
  yKey: string,
  trust: TailorTrustLimits = DEFAULT_TAILOR_TRUST_LIMITS,
): number | null {
  const raw = numericValue(row[output]);
  if (mode === "raw") return raw;
  const audit = correctionForPair(row, model, output, heldValues, xKey, yKey, trust);
  if (!audit) return null;
  if (mode === "compatible") return audit.compatible ? audit.rawValue : null;
  return audit.status === "rejected" ? null : audit.correctedValue;
}

export function summarizeTailorEvidence(
  rows: Record<string, unknown>[],
  model: TailorSliceModel | null,
  output: string,
  heldValues: Record<string, number>,
  xKey: string,
  yKey: string,
  trust: TailorTrustLimits = DEFAULT_TAILOR_TRUST_LIMITS,
): TailorEvidenceSummary {
  let raw = 0;
  let compatible = 0;
  let correctedTrusted = 0;
  let correctedFlagged = 0;
  let correctedRejected = 0;
  let rmsSum = 0;
  let auditCount = 0;
  let maxHiddenJump = 0;
  let maxCorrectionDelta = 0;
  for (const row of rows) {
    if (numericValue(row[output]) !== null) raw += 1;
    const audit = correctionForPair(row, model, output, heldValues, xKey, yKey, trust);
    if (!audit) continue;
    auditCount += 1;
    rmsSum += audit.hiddenRms;
    maxHiddenJump = Math.max(maxHiddenJump, audit.hiddenMaxDelta);
    maxCorrectionDelta = Math.max(maxCorrectionDelta, Math.abs(audit.correctionDelta));
    if (audit.compatible) compatible += 1;
    if (audit.status === "trusted") correctedTrusted += 1;
    else if (audit.status === "flagged") correctedFlagged += 1;
    else correctedRejected += 1;
  }
  return {
    raw,
    compatible,
    correctedTotal: correctedTrusted + correctedFlagged + correctedRejected,
    correctedTrusted,
    correctedFlagged,
    correctedRejected,
    meanHiddenRms: auditCount ? rmsSum / auditCount : null,
    maxHiddenJump: auditCount ? maxHiddenJump : null,
    maxCorrectionDelta: auditCount ? maxCorrectionDelta : null,
  };
}

const CANDIDATE_QUANTILES = [0.08, 0.24, 0.42, 0.58, 0.76, 0.92];

function candidateValues(rows: Record<string, unknown>[], axis: TailorAxisInput): number[] {
  const values = rows
    .map((row) => numericValue(row[axis.id]))
    .filter((value): value is number => value !== null)
    .sort((a, b) => a - b);
  const min = numericValue(axis.min) ?? axisDefault(axis) - axisSpan(axis) / 2;
  const max = numericValue(axis.max) ?? axisDefault(axis) + axisSpan(axis) / 2;
  const rangeValues = CANDIDATE_QUANTILES.map((q) => min + (max - min) * q);
  if (values.length < 6) return rangeValues;
  const observedSpread = values[values.length - 1]! - values[0]!;
  const observedValues = CANDIDATE_QUANTILES.map((q) => values[Math.min(values.length - 1, Math.max(0, Math.floor(q * (values.length - 1))))]!);
  // an axis the campaign never (or barely) moved must not imprison Tailor in
  // the flown plane: blend in range quantiles so "leave the plane" is sampleable
  if (observedSpread < 0.2 * axisSpan(axis)) {
    return Array.from(new Set([...observedValues, ...rangeValues]));
  }
  return observedValues;
}

/** Deterministic PRNG so suggestion lists are stable across re-renders. */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function recommendTailorRuns({
  rows,
  model,
  axes,
  heldValues,
  output,
  xKey,
  yKey,
  limit = 6,
  candidateCount = 320,
  trust = DEFAULT_TAILOR_TRUST_LIMITS,
}: {
  rows: Record<string, unknown>[];
  model: TailorSliceModel | null;
  axes: TailorAxisInput[];
  heldValues: Record<string, number>;
  output: string;
  xKey: string;
  yKey: string;
  limit?: number;
  candidateCount?: number;
  trust?: TailorTrustLimits;
}): TailorRunSuggestion[] {
  if (!model) return [];
  // Operate on the axes the model ACTUALLY fit. fitTailorSliceModel drops
  // axes with <3 samples, so model.axes can be a strict subset of `axes`.
  // Scoring/moving/auditing over the full `axes` would advertise moves on an
  // axis the model is blind to (predictTailorSliceModel and correctionForPair
  // both key off model.axes), and count a full-span excursion on a dropped
  // axis as "trusted" — invisibly. Stay on model.axes everywhere.
  const fitAxes = model.axes.length ? model.axes : axes;
  const xAxis = fitAxes.find((axis) => axis.id === xKey);
  const yAxis = fitAxes.find((axis) => axis.id === yKey);
  if (!xAxis || !yAxis || xAxis.id === yAxis.id) return [];
  // Tailor learns the FULL (fitted) axis space from the first sample: candidate
  // pools exist for every fitted axis (range quantiles when the data never
  // varied one), and distance is scored in normalized ND space — so "leave the
  // plane" can win against "densify the plane". The 2D pane is only a projection.
  const pools = new Map(fitAxes.map((axis) => [axis.id, candidateValues(rows, axis)]));
  const hiddenAxes = fitAxes.filter((axis) => axis.id !== xKey && axis.id !== yKey);
  const stride = Math.max(1, Math.ceil(rows.length / 1200));
  const observedRows = rows.filter((_, index) => index % stride === 0);
  const observedNorm = observedRows.map((row) => fitAxes.map((axis) => axisNorm(axisRaw(row, axis), axis)));
  const heldFor = (axis: TailorAxisInput) => numericValue(heldValues[axis.id]) ?? axisDefault(axis);
  const rand = mulberry32(rows.length * 7919 + fitAxes.length * 131 + xKey.length * 17 + yKey.length);
  const pick = (axis: TailorAxisInput) => {
    const pool = pools.get(axis.id)!;
    return pool[Math.floor(rand() * pool.length)] ?? heldFor(axis);
  };

  const candidates: Record<string, number>[] = [];
  // on-slice grid: the old behavior stays in the running as a candidate family
  for (const x of pools.get(xKey)!) {
    for (const y of pools.get(yKey)!) {
      const values: Record<string, number> = {};
      for (const axis of fitAxes) values[axis.id] = heldFor(axis);
      values[xKey] = x;
      values[yKey] = y;
      candidates.push(values);
    }
  }
  // ND proposals: move the pane axes plus 1-3 hidden axes off the held slice
  for (let draw = 0; draw < candidateCount; draw += 1) {
    const values: Record<string, number> = {};
    for (const axis of fitAxes) values[axis.id] = heldFor(axis);
    values[xKey] = pick(xAxis);
    values[yKey] = pick(yAxis);
    if (hiddenAxes.length) {
      const moveCount = 1 + Math.floor(rand() * Math.min(3, hiddenAxes.length));
      const order = [...hiddenAxes].sort(() => rand() - 0.5);
      for (const axis of order.slice(0, moveCount)) values[axis.id] = pick(axis);
    }
    candidates.push(values);
  }

  const suggestions: TailorRunSuggestion[] = [];
  const AUDIT_NEIGHBORS = 16;
  for (const values of candidates) {
    const candNorm = fitAxes.map((axis) => axisNorm(values[axis.id]!, axis));
    let nearest = Infinity;
    // K nearest rows in ND space, kept for the evidence audit below
    const nearestRows: { d2: number; index: number }[] = [];
    for (let rowIndex = 0; rowIndex < observedNorm.length; rowIndex += 1) {
      const obs = observedNorm[rowIndex]!;
      let sum = 0;
      for (let index = 0; index < candNorm.length; index += 1) {
        const delta = candNorm[index]! - obs[index]!;
        sum += delta * delta;
      }
      nearest = Math.min(nearest, sum);
      if (nearestRows.length < AUDIT_NEIGHBORS) {
        nearestRows.push({ d2: sum, index: rowIndex });
        if (nearestRows.length === AUDIT_NEIGHBORS) nearestRows.sort((a, b) => a.d2 - b.d2);
      } else if (sum < nearestRows[AUDIT_NEIGHBORS - 1]!.d2) {
        nearestRows[AUDIT_NEIGHBORS - 1] = { d2: sum, index: rowIndex };
        nearestRows.sort((a, b) => a.d2 - b.d2);
      }
    }
    nearest = Math.sqrt(nearest / Math.max(1, candNorm.length));
    // raw/compatible/corrected audit: can any nearby run stand in for this
    // candidate once Tailor transports it here? Sampling goes where even
    // CORRECTED evidence fails the trust gate, not merely where space is empty.
    const evidence: TailorSuggestionEvidence = { trusted: 0, flagged: 0, rejected: 0 };
    let nearestTrusted = Infinity;
    for (const entry of nearestRows) {
      const row = observedRows[entry.index]!;
      const audit = correctionForPair(row, model, output, values, xKey, yKey, trust);
      if (!audit) continue;
      const dx = axisNorm(axisRaw(row, xAxis), xAxis) - axisNorm(values[xKey]!, xAxis);
      const dy = axisNorm(axisRaw(row, yAxis), yAxis) - axisNorm(values[yKey]!, yAxis);
      const paneDistance = Math.sqrt(dx * dx + dy * dy);
      if (audit.compatible || audit.status === "trusted") {
        evidence.trusted += 1;
        nearestTrusted = Math.min(nearestTrusted, paneDistance);
      } else if (audit.status === "flagged") {
        evidence.flagged += 1;
      } else {
        evidence.rejected += 1;
      }
    }
    const trustedGap = Math.min(nearestTrusted, 1.2);
    let sliceSum = 0;
    const moves: TailorSuggestionMove[] = [];
    for (const axis of hiddenAxes) {
      const delta = axisNorm(values[axis.id]!, axis) - axisNorm(heldFor(axis), axis);
      sliceSum += delta * delta;
      if (Math.abs(delta) > 0.02) moves.push({ axis: axis.id, label: axis.label, value: values[axis.id]!, delta });
    }
    moves.sort((left, right) => Math.abs(right.delta) - Math.abs(left.delta));
    const sliceDistance = hiddenAxes.length ? Math.sqrt(sliceSum / hiddenAxes.length) : 0;
    const predicted = predictTailorSliceModel(model, values);
    const responseLift = Math.abs(predicted - model.outputMean) / Math.max(1e-9, model.outputScale);
    const score = trustedGap + 0.25 * nearest + 0.28 * responseLift;
    const moveSummary = moves.slice(0, 2).map((move) => move.label).join("/");
    const reason = evidence.trusted === 0
      ? `no trusted ${output} evidence within reach — needs a direct run${moveSummary ? ` (${moveSummary})` : ""}`
      : moves.length && nearest > 0.12
        ? `explores ${moveSummary} off the current slice`
        : nearest > 0.12
          ? `fills sparse ${xAxis.label}/${yAxis.label} slice evidence`
          : responseLift > 0.35
            ? `checks a high-response ${output} region`
            : `adds direct evidence at the held slice`;
    suggestions.push({
      id: fitAxes.map((axis) => `${axis.id}:${(values[axis.id] ?? 0).toPrecision(5)}`).join("|"),
      xAxis: xKey,
      yAxis: yKey,
      values,
      predicted,
      nearestDistance: nearest,
      nearestTrustedDistance: trustedGap,
      score,
      reason,
      sliceDistance,
      moves: moves.slice(0, 3),
      evidence,
    });
  }
  const seen = new Set<string>();
  return suggestions
    .sort((left, right) => right.score - left.score)
    .filter((item) => {
      const key = fitAxes.map((axis) => Math.round(axisNorm(item.values[axis.id]!, axis) * 25)).join("|");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
}

const RIDGE_SCAN_X = 64;
const RIDGE_Y_STATIONS = 9;

/**
 * Derive an HONEST departure frontier from the fitted quadratic response model.
 *
 * This is NOT a measured stall line. The RIDGE_TRACKING_RESULTS bakeoff defines
 * the aero-meaningful ridge as "the first persistent departure from the cheap
 * (linear) prior — where the cheap physics stops being right". The TS slice has
 * no GP and no probe ensemble, so we approximate that statistic from what the
 * surrogate we DO fit can support: along the X focus axis (holding Y at a
 * station and the hidden axes at the slice), find where the fitted response
 * bends hardest away from its own local linear trend. That bend is exactly the
 * curvature the quadratic term carries; a flat (purely linear) fit produces no
 * frontier, which is the honest answer. We trace the crest across Y stations to
 * get a manifold, and gate the whole thing on a confidence that reflects (a)
 * how much the surface actually bends relative to its range and (b) whether
 * Tailor's high-score suggestions cluster on the crest. The caller renders it
 * faint + labelled "model-derived" so it can never read as observation.
 */
export function deriveFeatureRidge({
  model,
  heldValues,
  output,
  xKey,
  yKey,
  suggestions = [],
  minConfidence = 0.12,
}: {
  model: TailorSliceModel | null;
  heldValues: Record<string, number>;
  output: string;
  xKey: string;
  yKey: string;
  suggestions?: TailorRunSuggestion[];
  minConfidence?: number;
}): TailorFeatureRidge | null {
  if (!model) return null;
  const xAxis = model.axes.find((axis) => axis.id === xKey);
  const yAxis = model.axes.find((axis) => axis.id === yKey);
  if (!xAxis || !yAxis || xAxis.id === yAxis.id) return null;
  const xMin = numericValue(xAxis.min);
  const xMax = numericValue(xAxis.max);
  const yMin = numericValue(yAxis.min);
  const yMax = numericValue(yAxis.max);
  if (xMin === null || xMax === null || yMin === null || yMax === null) return null;
  if (Math.abs(xMax - xMin) < 1e-12 || Math.abs(yMax - yMin) < 1e-12) return null;

  const heldFor = (axis: TailorAxisInput) => numericValue(heldValues[axis.id]) ?? axisDefault(axis);
  const base: Record<string, number> = {};
  for (const axis of model.axes) base[axis.id] = heldFor(axis);

  // Predicted response along X at a fixed Y station, on the held slice.
  const scanRow = (y: number, x: number): number =>
    predictTailorSliceModel(model, base, { [yKey]: y, [xKey]: x });

  const points: TailorRidgePoint[] = [];
  let peakBend = 0;
  let crestX = (xMin + xMax) / 2;
  let crestDepartureSum = 0;

  for (let yi = 0; yi < RIDGE_Y_STATIONS; yi += 1) {
    const y = yMin + ((yMax - yMin) * yi) / (RIDGE_Y_STATIONS - 1);
    // Sample the response along X and measure local departure from the linear
    // trend via the absolute second difference (discrete curvature).
    const xs: number[] = [];
    const ys: number[] = [];
    for (let xi = 0; xi < RIDGE_SCAN_X; xi += 1) {
      const x = xMin + ((xMax - xMin) * xi) / (RIDGE_SCAN_X - 1);
      xs.push(x);
      ys.push(scanRow(y, x));
    }
    // Discrete second difference ~ f''·(Δx)^2. Multiply by (N-1)^2 so the bend
    // approximates f''·span^2 (total curvature across the range) and is
    // independent of the scan resolution.
    const bendScale = (RIDGE_SCAN_X - 1) * (RIDGE_SCAN_X - 1);
    let bestBend = 0;
    let bestIndex = Math.floor(RIDGE_SCAN_X / 2);
    for (let xi = 1; xi < RIDGE_SCAN_X - 1; xi += 1) {
      const bend = Math.abs(ys[xi + 1]! - 2 * ys[xi]! + ys[xi - 1]!) * bendScale;
      if (bend > bestBend) {
        bestBend = bend;
        bestIndex = xi;
      }
    }
    // Normalize the bend by the response scale so it reads as 0..1 departure.
    // A surface whose total bend across the axis is ~half its output range is a
    // strong frontier (1.0); a near-linear surface barely registers.
    const departure = Math.min(1, bestBend / Math.max(1e-9, model.outputScale * 2));
    const crestForStation = xs[bestIndex]!;
    points.push({
      x: crestForStation,
      y,
      predicted: ys[bestIndex]!,
      departure,
    });
    crestDepartureSum += departure;
    if (departure > peakBend) {
      peakBend = departure;
      crestX = crestForStation;
    }
  }
  if (!points.length) return null;

  // Confidence from the average departure strength along the manifold.
  let confidence = crestDepartureSum / points.length;
  // Boost if Tailor's highest-score suggestions sit near the crest in X — the
  // sampler independently agreeing the action is here is corroboration.
  const xSpan = Math.abs(xMax - xMin);
  const nearCrest = suggestions
    .slice(0, 4)
    .filter((item) => {
      const sx = numericValue(item.values[xKey]);
      return sx !== null && Math.abs(sx - crestX) <= xSpan * 0.18;
    }).length;
  if (suggestions.length) confidence = Math.min(1, confidence + 0.12 * nearCrest);

  if (confidence < minConfidence) return null;

  const strength = confidence >= 0.5 ? "a clear" : confidence >= 0.28 ? "a moderate" : "a faint";
  const caption =
    `Model-derived departure frontier (not measured): ${strength} bend in the fitted ` +
    `${output} response near ${xAxis.label}≈${crestX.toPrecision(3)}, where a linear ` +
    `extrapolation of the cheap surrogate stops holding. Confidence ${(confidence * 100).toFixed(0)}%.`;

  return {
    xAxis: xKey,
    yAxis: yKey,
    output,
    points,
    crestX,
    confidence,
    derivation: "model-quadratic-departure",
    caption,
  };
}

/**
 * Build a click-to-adopt resolver for plotted suggestions. Given the current
 * suggestion list, returns `(id, heldValues) => nextHeldValues` that merges the
 * adopted suggestion's full ND values into the held slice (mirrors the text
 * list's adopt button). Unknown ids are a no-op (returns heldValues unchanged).
 * Pure + exported so the click-to-adopt path is regression-guarded.
 */
export function buildSuggestionAdopter(
  suggestions: TailorRunSuggestion[],
): (id: string, heldValues: Record<string, number>) => Record<string, number> {
  const byId = new Map(suggestions.map((item) => [item.id, item.values]));
  return (id, heldValues) => {
    const values = byId.get(id);
    if (!values) return heldValues;
    return { ...heldValues, ...values };
  };
}
